import CancelablePromise from 'cancelable-promise';
import getProgressReport from '../../api/getProgressReport';
import {createQueryError} from '../../lib/queryClient';
import {
	type IProgressReport,
	type IProgressReportProgressMessage,
} from '@netx/core-schema';

const DEFAULT_INTERVAL = 500;

const defaultCheckCb = (progressReport: IProgressReport) =>
	progressReport.percentComplete === 100;

export type PollUntilParams<WarningObject extends object = object> = {
	uuid: string;
	interval?: number;
	throwOnWarnings?: boolean;
	checkCb?: (progressReport: IProgressReport<WarningObject>) => boolean;
	onSuccess?: (progressReport: IProgressReport<WarningObject>) => void;
	onError?: (error: any) => void;
	onProgress?: (progressReport: IProgressReport<WarningObject>) => void;
};

export const getProgressMessages = (
	progressReport: IProgressReport,
): IProgressReportProgressMessage[] => progressReport.progressMessages || [];

export const getFirstProgressMessage = (
	progressReport: IProgressReport,
): IProgressReportProgressMessage | undefined =>
	getProgressMessages(progressReport)[0];

export const getObjectIdFromProgressReport = (
	progressReport: IProgressReport,
	shouldThrow = false,
): number => {
	const objectId = getFirstProgressMessage(progressReport)?.objectId;
	if (objectId == null && shouldThrow)
		throw new Error('`objectId` not found in progress report');
	return objectId ?? -1;
};

export const pollUntil = <WarningObject extends object = object>({
	throwOnWarnings = false,
	uuid,
	checkCb = defaultCheckCb,
	interval = DEFAULT_INTERVAL,
	onSuccess,
	onError,
	onProgress,
}: PollUntilParams<WarningObject>): CancelablePromise<
	IProgressReport<WarningObject>
> =>
	new CancelablePromise<IProgressReport<WarningObject>>(
		(resolve, reject, onCancel) => {
			let progress = 0;
			let timeout: NodeJS.Timeout | null = null;
			let abortController: AbortController | null = null;

			const _clearTimeout = () => {
				if (!timeout) return;
				clearTimeout(timeout);
				timeout = null;
			};

			const abort = () => {
				if (!abortController) return;
				abortController.abort();
				abortController = null;
			};

			onCancel(() => {
				_clearTimeout();
				abort();
			});

			const poll = async () => {
				abortController = new AbortController();

				try {
					const progressReport = await getProgressReport<WarningObject>(
						{uuid},
						abortController.signal,
					);

					// check if progress has changed
					if (progress !== progressReport.percentComplete) {
						progress = progressReport.percentComplete;
						onProgress?.(progressReport);
					}

					// check if we should continue polling
					if (!checkCb(progressReport)) {
						timeout = setTimeout(poll, interval);
						return;
					}

					if (
						throwOnWarnings &&
						progressReport.warningObject != null &&
						!!Object.keys(progressReport.warningObject).length
					) {
						throw createQueryError(
							(progressReport.warningObject as any).message ||
								'Progress report contains warnings',
							(progressReport.warningObject as any).code ?? -1,
						);
					}

					// if we reach here, polling is done
					onSuccess?.(progressReport);
					resolve(progressReport);
				} catch (ex) {
					onError?.(ex);
					reject(ex);
				}
			};

			poll();
		},
	);
